import { Injectable, signal } from '@angular/core';
import { inject } from '@angular/core/primitives/di';
import { Observable } from 'rxjs';
import { ApiService } from 'shared-lib';
import { OtpService } from '../../../shared/services/otp.service';

export type ForgetPasswordScreenType = 'MOBILE_INPUT' | 'OTP' | 'SET_PASSWORD';

export const FORGET_PASSWORD_SCREEN = {
  MOBILE_INPUT: 'MOBILE_INPUT',
  OTP: 'OTP',
  SET_PASSWORD: 'SET_PASSWORD',
} as const;

export type ForgetPasswordInfo = {
  phoneNumber?: string;
  otp?: string;
  otpToken?: string;
  password?: string;
  otpSessionId?: string;
  otpLength?: number;
  expiryTimeInSeconds?: number;
  resendIntervalInSeconds?: number;
  userId?: number;
  isSid?: boolean;
  otpValidated?: boolean;
  sentPhoneNumber?: string;
};

@Injectable({
  providedIn: 'root',
})
export class ForgetPasswordService {
  readonly currentScreen = signal<ForgetPasswordScreenType>(FORGET_PASSWORD_SCREEN.MOBILE_INPUT);
  readonly userDetails = signal<ForgetPasswordInfo>(null);

  private readonly apiService = inject(ApiService);
  private readonly otpService = inject(OtpService);

  updateScreen(screenType: ForgetPasswordScreenType): void {
    this.currentScreen.set(screenType);
  }

  sendOtp(): Observable<any> {
    const phoneNumber = this.userDetails()?.phoneNumber;
    return this.otpService.sendOtp(phoneNumber);
  }

  validateOtp(otp: string): Observable<any> {
    const userDetails = this.userDetails();
    return this.otpService.validateOtp(otp, userDetails?.otpToken, userDetails?.userId?.toString());
  }

  resendOtp(): Observable<any> {
    const userDetails = this.userDetails();
    return this.otpService.resendOtp(userDetails?.phoneNumber, undefined, undefined, userDetails?.otpToken);
  }

  resetPassword(newPassword: string): Observable<any> {
    const payload = {
      userId: this.userDetails()?.userId,
      otpToken: this.userDetails()?.otpToken,
      newPassword: newPassword,
    };
    return this.apiService.post(`auth/forgot-password`, payload);
  }

  hasOtpState(): boolean {
    const details = this.userDetails();
    return !!details?.phoneNumber && !!details?.otpToken;
  }

  hasPasswordState(): boolean {
    const details = this.userDetails();
    return this.hasOtpState() && !!details?.otpValidated;
  }

  reset(): void {
    this.currentScreen.set(FORGET_PASSWORD_SCREEN.MOBILE_INPUT);
    this.userDetails.set(null);
  }
}
